import React, { useState, useEffect } from 'react';
import { Outlet, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useIsAuthenticated } from '@soundbyte/stores';
import { ROUTES, MAIN_SITE_URL } from '@soundbyte/config/constants';
import emblemWhite from '@/assets/emblem-white.png';
import logoIcon from '@/assets/logo-icon.png';

import gallery1 from '@/assets/gallery-1.jpg';
import gallery2 from '@/assets/gallery-2.jpg';
import gallery3 from '@/assets/gallery-3.jpg';
import gallery4 from '@/assets/gallery-4.jpg';
import soundbyte1 from '@/assets/soundbyte-1.jpg';
import soundbyte2 from '@/assets/soundbyte-2.jpg';
import soundbyte3 from '@/assets/soundbyte-3.jpg';
import soundbyte4 from '@/assets/soundbyte-4.jpg';

const galleryImages = [gallery1, soundbyte1, gallery2, soundbyte2, gallery3, soundbyte3, gallery4, soundbyte4];

// Routes that an authenticated user must still be allowed to reach — otherwise
// a logged-in user who clicks the reset-password link in their email would be
// silently bounced to the dashboard before the token can be consumed.
const AUTHED_ALLOWED_PATHS = ['/reset-password', '/forgot-password'];

export const AuthLayout: React.FC = () => {
  const isAuthenticated = useIsAuthenticated();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [currentIndex, setCurrentIndex] = useState(0);

  // Auto-cycle images every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % galleryImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Redirect to appropriate page if already logged in — but allow the password
  // reset / forgot flows through so authenticated sessions can still consume them.
  const onAllowedAuthedPath = AUTHED_ALLOWED_PATHS.some(p => location.pathname.startsWith(p));
  if (isAuthenticated && !onAllowedAuthedPath) {
    const journey = searchParams.get('journey');
    const story = searchParams.get('story');

    if (journey === 'sonic' && story) {
      return <Navigate to={`${ROUTES.SONIC}?story=${encodeURIComponent(story)}`} replace />;
    }
    if (journey === 'originals') {
      return <Navigate to={ROUTES.ORIGINALS} replace />;
    }
    if (journey === 'sonic') {
      return <Navigate to={ROUTES.SONIC} replace />;
    }
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Rotating Gallery */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-black">
        {/* Rotating artwork images */}
        <AnimatePresence mode="wait">
          <motion.img
            key={currentIndex}
            src={galleryImages[currentIndex]}
            alt="Sonic Identity artwork"
            className="absolute inset-0 w-full h-full object-cover"
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 1.2, ease: 'easeInOut' }}
          />
        </AnimatePresence>

        {/* Gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/40 z-10" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/30 to-transparent z-10" />

        {/* Content overlay */}
        <div className="relative z-20 flex flex-col justify-between p-12 w-full">
          {/* Logo */}
          <a href={MAIN_SITE_URL} className="flex items-center gap-3">
            <img src={emblemWhite} alt="amrita sethi" className="h-10 w-10" />
            <span className="text-xl font-semibold text-white">
              SoundBYTE
            </span>
          </a>

          {/* Bottom content */}
          <div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="font-serif text-4xl font-bold text-white mb-4 leading-tight"
            >
              Transform Your Voice<br />Into Art
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-white/80 text-base max-w-sm mb-8"
            >
              Create unique Sonic Identities with amrita sethi's
              proprietary voice-to-art system.
            </motion.p>

            {/* Dot indicators */}
            <div className="flex items-center gap-2">
              {galleryImages.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentIndex(i)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    i === currentIndex
                      ? 'w-8 bg-white'
                      : 'w-2 bg-white/40 hover:bg-white/60'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 bg-background relative overflow-hidden">
        {/* Subtle background decoration */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-primary/5 blur-[120px]" />
          <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full bg-primary/3 blur-[100px]" />
        </div>

        <div className="w-full max-w-md relative z-10">
          {/* Mobile logo */}
          <div className="flex justify-center mb-8 lg:hidden">
            <a href={MAIN_SITE_URL} className="flex items-center gap-3">
              <img src={logoIcon} alt="amrita sethi" className="h-10 w-10" />
              <span className="text-xl font-semibold text-foreground">
                SoundBYTE
              </span>
            </a>
          </div>

          {/* Form content (from child routes) */}
          <Outlet />

          {/* Back to site link - mobile */}
          <div className="mt-8 text-center lg:hidden">
            <a
              href={MAIN_SITE_URL}
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              Back to main website
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
